/**
 * Worksheet feature types (camelCase). The API serves money as integer cents;
 * these mirror the read shape after `api.ts` unwraps the envelope and maps
 * snake→camel. Line-item money fields are kept as cents here and converted to
 * dollar strings only at the form edge.
 */

export type Switch = "U" | "Y" | "N";

export const SWITCH_OPTIONS: { value: Switch; label: string }[] = [
  { value: "U", label: "Unknown" },
  { value: "Y", label: "Yes" },
  { value: "N", label: "No" },
];

export type WorksheetTab =
  | "personal"
  | "financial"
  | "assets"
  | "banking"
  | "incexp"
  | "offer";

// --- header ------------------------------------------------------------------

export interface Worksheet {
  id: string;
  case: string;
  client: string;
  // section-presence gates
  dependentSw: Switch;
  employedSw: Switch;
  spEmployedSw: Switch;
  otherBusSw: Switch;
  propertySw: Switch;
  vehicleSw: Switch;
  otherAssetSw: Switch;
  accountSw: Switch;
  investmentSw: Switch;
  virtualSw: Switch;
  retirementSw: Switch;
  creditSw: Switch;
  insuranceSw: Switch;
  transferSw: Switch;
  // financial circumstances
  lawsuitSw: Switch;
  bankruptNowSw: Switch;
  bankruptSw: Switch;
  benefitSw: Switch;
  trusteeSw: Switch;
  fundsHeldSw: Switch;
  safeDepSw: Switch;
  abroadSw: Switch;
  nonusSw: Switch;
  bankruptcyType: "" | "D" | "C";
  bankruptcyFiled: string | null;
  bankruptcyDismissed: string | null;
  bankruptcyPetition: string;
  bankruptcyLocation: string;
  trustName: string;
  trustAmount: number; // cents
  trustDate: string | null;
  trustTrustee: string;
  fundsHeldAmount: number; // cents
  fundsHeldLocation: string;
  safeDepositName: string;
  safeDepositAddress: string;
  safeDepositBox: string;
  safeDepositContent: string;
  safeDepositValue: number; // cents
  abroadFrom: string | null;
  abroadTo: string | null;
  nonusAssetDesc: string;
  // banking / offer
  cashOnHand: number; // cents
  offerPlan: number;
  explanation: string;
}

/** A single-field (or few-field) autosave patch to the header. Keys are the
 * snake_case API field names; money values are cents. */
export type WorksheetPatch = Record<string, string | number | boolean | null>;

// --- income / expense --------------------------------------------------------

export interface VariableRow {
  desc: string;
  amount: number; // cents
}

/** The income/expense grid — every money field is cents. Indexed access keeps
 * the ~90-field grid manageable in the UI without enumerating each here. */
export interface IncomeExpense {
  id: string;
  otherIncome: VariableRow[];
  otherAllowed: VariableRow[];
  // all remaining money fields (cents), keyed by snake_case API name
  [field: string]: number | string | VariableRow[];
}

// --- line items --------------------------------------------------------------

export interface TaxDebt {
  id: string;
  taxYear: number;
  amountOwed: number; // cents
  csed: string | null;
  included: boolean;
}

export interface Property {
  id: string;
  description: string;
  title: string;
  address: string;
  currentValue: number;
  loanBalance: number;
  monthlyPmt: number;
  finalPmtDate: string | null;
  lender: string;
  lenderContact: string;
  incomeGen: boolean;
  rentExp: number;
}

export interface Vehicle {
  id: string;
  description: string;
  title: string;
  year: number | null;
  make: string;
  model: string;
  currentValue: number;
  loanBalance: number;
  leaseLoan: "OWN" | "LEASE";
  incomeGen: boolean;
}

export interface OtherAsset {
  id: string;
  description: string;
  title: string;
  currentValue: number;
  loanBalance: number;
}

export type AccountClass = "P" | "I" | "V" | "R" | "C";

export interface FinancialAccount {
  id: string;
  accountClass: AccountClass;
  institution: string;
  accountType: string;
  accountNumberLabel: string; // masked, read-only
  accountValue: number;
  loanBalance: number;
  creditLimit: number;
  qsRate: string; // percent, decimal string
  keyAccess: boolean;
}

export interface InsurancePolicy {
  id: string;
  company: string;
  policyNo: string;
  cashValue: number;
  loanBalance: number;
}

export interface AssetTransfer {
  id: string;
  transferType: "N" | "R";
  description: string;
  amount: number;
  transferDate: string | null;
  toWhom: string;
}

export interface Lawsuit {
  id: string;
  description: string;
  amount: number;
  dateFiled: string | null;
}

export interface OtherBusiness {
  id: string;
  percent: string; // decimal
  title: string;
  name: string;
  phone: string;
  einLabel: string; // masked, read-only
  entityType: "" | "P" | "L" | "C" | "O";
  address: string;
}

// --- computed summaries ------------------------------------------------------

export interface Allowances {
  householdSize: number;
  food: number;
  housekeeping: number;
  apparel: number;
  personalCare: number;
  misc: number;
  housing: number;
  vehicleOperating: number;
  vehicleOwnership: number;
  publicTransit: number;
  healthOop: number;
}

export interface IaWorksheetRow {
  taxYear: number;
  amount: number; // cents
  cumulative: number; // cents
  months: number;
  monthly: number; // cents
}

export interface OfferSummary {
  rmiCents: number;
  totalAssetsCents: number;
  offerAmountCents: number;
  offerPlanMonths: number;
  iaType: "GIA" | "SIA" | "NSIA" | "IA";
  iaTypeLabel: string;
  iaCondition: string;
  totalDueCents: number;
  monthlyPaymentCents: number;
  calculatedMonths: number;
  maxMonths: number;
  interestRate: string;
  missingCseds: number[];
  expiredCseds: number[];
  warnings: string[];
  taxDebts: TaxDebt[];
  iaWorksheet: IaWorksheetRow[];
  allowances: Allowances;
}

// --- reused Client-hub records (Personal & Employment tab) -------------------

export interface Dependent {
  id: string;
  name: string;
  ssnLabel: string;
  age: number | null;
  relationship: string;
  claimedOnReturn: boolean;
  contributesToHousehold: boolean;
}

export interface Employer {
  id: string;
  belongsTo: "TAXPAYER" | "SPOUSE";
  name: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  county: string;
  phone: string;
  occupation: string;
  einLabel: string;
  payPeriod: "" | "WEEKLY" | "BIWEEKLY" | "SEMIMONTHLY" | "MONTHLY";
  grossWages: string; // decimal string from the clients API
  federalTax: string;
  stateTax: string;
  localTax: string;
  noFica: boolean;
  noMedicare: boolean;
}

export interface PayStub {
  id: string;
  item: string;
  category: string;
  amount: string; // decimal string
}

export const ACCOUNT_CLASS_OPTIONS: { value: AccountClass; label: string }[] = [
  { value: "P", label: "Checking / Savings" },
  { value: "I", label: "Investment" },
  { value: "V", label: "Virtual / Digital currency" },
  { value: "R", label: "Retirement" },
  { value: "C", label: "Available credit (card)" },
];

export const PAY_PERIOD_OPTIONS = [
  { value: "WEEKLY", label: "Weekly" },
  { value: "BIWEEKLY", label: "Biweekly" },
  { value: "SEMIMONTHLY", label: "Semimonthly" },
  { value: "MONTHLY", label: "Monthly" },
];

export const ENTITY_TYPE_OPTIONS = [
  { value: "P", label: "Partnership" },
  { value: "L", label: "LLC" },
  { value: "C", label: "Corporation" },
  { value: "O", label: "Other" },
];
